import cn from 'classnames';
import { useState, useContext, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import Heading from 'components/common/Heading';
import Subheading from 'components/common/Subheading';
import ToggleButton from 'components/common/ToggleButton';
import { VoucherContext } from 'components/signUpFunnel/Contexts';
import { TEMPO_PLAN_MEALS_PER_WEEK } from 'lib/constants/signUp';

import { calculatePricePerServingDetails } from '../helpers/calculatePricePerServingDetails';

const TempoBoxCustomizer = ({
  weeklyMeals,
  setWeeklyMeals,
  mealPriceDetails,
  boxRecommendations,
  isPickMeals = false,
  setShowBoxOptions,
}) => {
  const [showMorePlans, setShowMorePlans] = useState(false);
  const transitionRef = useRef();

  const boxDisplayOrder = boxRecommendations.displayOrder || TEMPO_PLAN_MEALS_PER_WEEK;

  const initialMealsPerWeek = boxDisplayOrder.slice(0, 4);
  const lastMealsPerWeek = boxDisplayOrder.slice(4);

  const containerText = {
    header: 'Recommended Box Size',
    subHeader:
      'Recommended based on your answers. You can add or remove meals before your order is finalized.',
    subQuestion: 'Your single-serving weekly meal plan',
  };

  const handleShowMoreToggle = () => {
    const newState = !showMorePlans;
    setShowMorePlans(newState);
    if (setShowBoxOptions) {
      setShowBoxOptions(newState);
    }
  };

  return (
    <>
      {!isPickMeals && (
        <>
          <Heading className="mb-2">{containerText.header}</Heading>
          <Subheading
            className={cn('mx-auto mb-6 max-w-md px-4 text-center')}
            brandStylesOverrides="textSm"
          >
            {containerText.subHeader}
          </Subheading>
          <Subheading className="textMd mb-4 px-4 text-left">
            {containerText.subQuestion}
          </Subheading>
        </>
      )}
      <div
        className="mx-auto grid grid-cols-2 gap-4 pb-4 bp1:mx-0"
        role="radiogroup"
        aria-label="Recipes options"
      >
        {initialMealsPerWeek.map(mealsPerWeek => (
          <MealsPerWeekButton
            key={mealsPerWeek}
            boxRecommendations={boxRecommendations}
            mealsPerWeek={mealsPerWeek}
            mealPriceDetails={mealPriceDetails}
            weeklyMeals={weeklyMeals}
            setWeeklyMeals={setWeeklyMeals}
          />
        ))}
      </div>
      <CSSTransition
        in={showMorePlans}
        timeout={500}
        classNames={{
          enter: 'fadeInOut-enter scaleHeight-enter',
          enterActive: 'fadeInOut-enter-active scaleHeight-enter-active',
          exit: 'fadeInOut-exit scaleHeight-exit',
          exitActive: 'fadeInOut-exit-active scaleHeight-exit-active',
        }}
        unmountOnExit
        nodeRef={transitionRef}
      >
        <div
          className="mx-auto grid grid-cols-2 gap-4 bp1:mx-0"
          role="radiogroup"
          aria-label="Recipes options"
          ref={transitionRef}
        >
          {lastMealsPerWeek.map(mealsPerWeek => (
            <MealsPerWeekButton
              key={mealsPerWeek}
              boxRecommendations={boxRecommendations}
              mealsPerWeek={mealsPerWeek}
              mealPriceDetails={mealPriceDetails}
              weeklyMeals={weeklyMeals}
              setWeeklyMeals={setWeeklyMeals}
            />
          ))}
        </div>
      </CSSTransition>
      <button
        className="h7 flex w-full cursor-pointer justify-center pr-1 pt-4 text-tempo-black-100 underline outline-none bp1:pr-10 bp2:pr-6"
        onClick={handleShowMoreToggle}
      >
        {showMorePlans ? 'Show Less' : 'Show More'}
      </button>
    </>
  );
};

const MealsPerWeekButton = ({
  boxRecommendations,
  mealsPerWeek,
  mealPriceDetails,
  setWeeklyMeals,
  weeklyMeals,
}) => {
  const { voucher } = useContext(VoucherContext);
  const priceDetail = calculatePricePerServingDetails(mealPriceDetails, mealsPerWeek, voucher);

  return (
    <ToggleButton
      className={cn(
        'relative mb-0 flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded border border-solid p-1 px-2 text-center bp1:mx-0'
      )}
      id={mealsPerWeek}
      selected={mealsPerWeek === weeklyMeals}
      handleClick={() => setWeeklyMeals(mealsPerWeek)}
      type="box"
      key={mealsPerWeek}
      aria-label={`${mealsPerWeek} recipes`}
      role="radio"
    >
      {mealsPerWeek === boxRecommendations.recommendedMeals && (
        <span className="h7 absolute left-0 right-0 top-0 rounded-t-sm bg-tempo-navy-100 py-1 text-white-100">
          RECOMMENDED
        </span>
      )}
      <span className="text-tempo-black-100">{`${mealsPerWeek} meal plan`}</span>
      <span className="block">
        <span
          className={cn({
            'font-normal text-tempo-black-100 line-through': priceDetail.hasPromo,
            'text-tempo-blue-90': !priceDetail.hasPromo,
          })}
        >
          {priceDetail.mealPriceFormatted}
        </span>
        {priceDetail.hasPromo && (
          <span className="pl-1 text-tempo-blue-90">{priceDetail.promoMealPriceFormatted}</span>
        )}
        <span className="text-tempo-blue-90"> ea</span>
      </span>
    </ToggleButton>
  );
};

TempoBoxCustomizer.propTypes = {
  weeklyMeals: PropTypes.number.isRequired,
  setWeeklyMeals: PropTypes.func.isRequired,
  mealPriceDetails: PropTypes.array,
  boxRecommendations: PropTypes.shape({
    recommendedMeals: PropTypes.number,
    displayOrder: PropTypes.arrayOf(PropTypes.number),
  }),
  isPickMeals: PropTypes.bool,
  setShowBoxOptions: PropTypes.func,
};

MealsPerWeekButton.propTypes = {
  weeklyMeals: PropTypes.number.isRequired,
  setWeeklyMeals: PropTypes.func.isRequired,
  mealPriceDetails: PropTypes.array,
  boxRecommendations: PropTypes.shape({
    recommendedMeals: PropTypes.number,
    displayOrder: PropTypes.arrayOf(PropTypes.number),
  }),
  mealsPerWeek: PropTypes.number.isRequired,
};

export default TempoBoxCustomizer;
